import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Receipt as InvoiceIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import InvoiceService from '../../services/invoice.service';
import DarbInvoiceTemplate from '../../components/invoices/DarbInvoiceTemplate';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';
import { getBillToFromInvoice } from '../../utils/invoiceBillTo';

const InvoiceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<any>(null);
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchInvoice();
      fetchLineItems();
    }
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await InvoiceService.getOne(Number(id), {
        populate: ['customer', 'insuranceClient', 'insuranceClient.insuranceCompany', 'serviceProvider', 'relatedRequests'],
      });
      setInvoice(response.data?.data);
    } catch (error: any) {
      console.error('Error fetching invoice:', error);
      setError(error.response?.data?.error?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const fetchLineItems = async () => {
    try {
      const response = await InvoiceService.getItems(Number(id));
      setLineItems(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching line items:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await InvoiceService.delete(Number(id));
        navigate('/invoices');
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('Failed to delete invoice');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'sent':
        return 'info';
      case 'draft':
        return 'default';
      case 'partiallyPaid':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async (invoiceType: 'customer' | 'insurance' = 'customer') => {
    if (!invoice) return;

    const doc = new jsPDF();
    const data = invoice.attributes;
    const billTo = getBillToFromInvoice(invoice);
    const relatedRequests = data.relatedRequests?.data || data.relatedRequests || [];
    const firstRequest = relatedRequests.length > 0 ? relatedRequests[0] : null;

    // Load and add logo
    await addLogoToPDF(doc);

    // Add company name
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

    // Add invoice type header
    if (invoiceType === 'insurance') {
      doc.setFontSize(14);
      doc.setTextColor(33, 150, 243);
      doc.text('INSURANCE INVOICE', 105, 20, { align: 'center' });
    }

    // Add invoice details on the right
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`DATE: ${formatDate(data.invoiceDate)}`, 175, invoiceType === 'insurance' ? 30 : 20, { align: 'right' });
    doc.text(`INVOICE #: ${data.invoiceNumber}`, 175, invoiceType === 'insurance' ? 37 : 27, { align: 'right' });
    doc.text(`Status: ${(data.status || 'draft').toUpperCase()}`, 175, invoiceType === 'insurance' ? 44 : 34, { align: 'right' });

    // Add company details
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let companyY = invoiceType === 'insurance' ? 58 : 48;
    doc.text('CR: 1366462', 20, companyY);
    companyY += 5;
    doc.text('BLOCK 255, STREET, WAY 5508, BUILDING 564', 20, companyY);
    companyY += 5;
    doc.text('VAT NUM: OM1100429932', 20, companyY);
    companyY += 5;
    if (data.omanTaxId) {
      doc.text(`Oman Tax ID: ${data.omanTaxId}`, 20, companyY);
      companyY += 5;
    }
    doc.text('Phone: 80081818 - 24790888', 20, companyY);
    companyY += 5;
    doc.text('Email: INFO@DARBNS.COM', 20, companyY);

    // Add bill-to information (from insuranceClient, customer, serviceProvider, or customerName)
    let billToY = (invoiceType === 'insurance' ? 90 : 75) + (data.omanTaxId ? 5 : 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('BILL TO:', 20, billToY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPos = billToY + 7;
    doc.text(billTo?.name || '-', 20, yPos);

    if (invoiceType === 'insurance' && billTo?.insuranceCompany) {
      yPos += 5;
      doc.setFont('helvetica', 'bold');
      doc.text(`Insurance Company: ${billTo.insuranceCompany}`, 20, yPos);
      doc.setFont('helvetica', 'normal');
    }
    if (invoiceType === 'insurance' && billTo?.policyNumber) {
      yPos += 5;
      doc.text(`Policy Number: ${billTo.policyNumber}`, 20, yPos);
    }
    if (invoiceType === 'insurance' && billTo?.vinNumber) {
      yPos += 5;
      doc.text(`VIN Number: ${billTo.vinNumber}`, 20, yPos);
    }
    if (billTo?.email) {
      yPos += 5;
      doc.text(`Email: ${billTo.email}`, 20, yPos);
    }
    if (billTo?.phone) {
      yPos += 5;
      doc.text(`Phone: ${billTo.phone}`, 20, yPos);
    }
    if (invoiceType === 'customer' && billTo?.vinNumber) {
      yPos += 5;
      doc.text(`VIN: ${billTo.vinNumber}`, 20, yPos);
    }
    if (billTo?.address) {
      yPos += 5;
      doc.text(`Address: ${billTo.address}`, 20, yPos);
    }

    // Get request data for service information
    const requestAttrs = firstRequest?.attributes || firstRequest;
    const serviceCode = requestAttrs?.serviceCode;
    const serviceNames: { [key: string]: string } = {
      'TOWING_SERVICE': 'Vehicle Towing',
      'BATTERY_BOOST': 'Battery Boost',
      'LOCKOUT_SERVICE': 'Lockout Assistance',
      'FUEL_DELIVERY': 'Fuel Delivery',
    };
    const serviceName = serviceCode ? serviceNames[serviceCode] || serviceCode : '';

    // Add line items table
    const tableData = lineItems.map((item) => {
      const attrs = item.attributes;
      const product = attrs?.product?.data?.attributes;
      const description = product?.name || attrs?.description || '-';
      const unitPrice = attrs?.unitPrice || 0;
      
      if (invoiceType === 'insurance') {
        return [
          description,
          serviceName || '-',
          `OMR ${unitPrice.toFixed(2)}`,
          `OMR ${(attrs?.total || 0).toFixed(2)}`,
        ];
      } else {
        return [
          description,
          attrs?.quantity || 0,
          `OMR ${unitPrice.toFixed(2)}`,
          `OMR ${(attrs?.discount || 0).toFixed(2)}`,
          `OMR ${(attrs?.tax || 0).toFixed(2)}`,
          `OMR ${(attrs?.total || 0).toFixed(2)}`,
        ];
      }
    });

    const tableHeaders = invoiceType === 'insurance' 
      ? [['DESCRIPTION', 'SERVICE', 'RATE', 'AMOUNT']]
      : [['Product', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total']];

    autoTable(doc, {
      startY: yPos + 10,
      head: tableHeaders,
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: 255,
        fontStyle: 'bold',
      },
      styles: {
        fontSize: 9,
      },
      columnStyles: invoiceType === 'insurance' 
        ? {
            0: { cellWidth: 60 },
            1: { cellWidth: 40 },
            2: { halign: 'right', cellWidth: 30 },
            3: { halign: 'right', cellWidth: 30 },
          }
        : {
            0: { cellWidth: 60 },
            1: { halign: 'right', cellWidth: 20 },
            2: { halign: 'right', cellWidth: 25 },
            3: { halign: 'right', cellWidth: 25 },
            4: { halign: 'right', cellWidth: 25 },
            5: { halign: 'right', cellWidth: 25 },
          },
    });

    // Add totals
    const finalY = (doc as any).lastAutoTable.finalY || yPos + 10;
    doc.setFontSize(10);
    
    doc.text('Subtotal:', 130, finalY + 10);
    doc.text(`OMR ${(data.subtotal || 0).toFixed(2)}`, 180, finalY + 10, { align: 'right' });
    
    if (invoiceType === 'customer') {
      doc.text('Discount:', 130, finalY + 17);
      doc.text(`- OMR ${(data.discount || 0).toFixed(2)}`, 180, finalY + 17, { align: 'right' });
    }
    
    doc.text('Tax:', 130, finalY + (invoiceType === 'customer' ? 24 : 17));
    doc.text(`OMR ${(data.tax || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 24 : 17), { align: 'right' });
    
    // Total line
    doc.setDrawColor(33, 150, 243);
    doc.setLineWidth(0.5);
    doc.line(130, finalY + (invoiceType === 'customer' ? 27 : 20), 180, finalY + (invoiceType === 'customer' ? 27 : 20));
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total:', 130, finalY + (invoiceType === 'customer' ? 34 : 27));
    doc.text(`OMR ${(data.total || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 34 : 27), { align: 'right' });

    // Payment info
    if (data.paidAmount > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Paid Amount:', 130, finalY + (invoiceType === 'customer' ? 44 : 37));
      doc.text(`OMR ${(data.paidAmount || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 44 : 37), { align: 'right' });
      
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 0, 0);
      doc.text('Balance Due:', 130, finalY + (invoiceType === 'customer' ? 51 : 44));
      doc.text(`OMR ${(data.balanceDue || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 51 : 44), { align: 'right' });
      doc.setTextColor(0, 0, 0);
    }

    // Add notes if any
    if (data.notes) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 20, finalY + (invoiceType === 'customer' ? 65 : 58));
      doc.setFont('helvetica', 'normal');
      const splitNotes = doc.splitTextToSize(data.notes, 170);
      doc.text(splitNotes, 20, finalY + (invoiceType === 'customer' ? 72 : 65));
    }

    // Save the PDF
    const fileName = invoiceType === 'insurance' 
      ? `Insurance-Invoice-${data.invoiceNumber}.pdf`
      : `Customer-Invoice-${data.invoiceNumber}.pdf`;
    doc.save(fileName);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
          sx={{ mb: 2 }}
        >
          Back to Invoices
        </Button>
        <Alert severity="error">{error || 'Invoice not found'}</Alert>
      </Box>
    );
  }

  const data = invoice.attributes;
  const billTo = getBillToFromInvoice(invoice);
  const relatedRequests = data.relatedRequests?.data || data.relatedRequests || [];
  const firstRequest = relatedRequests.length > 0 ? relatedRequests[0] : null;

  return (
    <Box>
      {/* Print-specific styles */}
      <style>
        {`
          .print-only {
            display: none;
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-area, .print-area * {
              visibility: visible;
            }
            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            @page {
              margin: 1cm;
            }
          }
        `}
      </style>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')}>
            Back
          </Button>
          <Typography variant="h4">
            <InvoiceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {data.invoiceNumber}
          </Typography>
          <Chip
            label={data.status || 'draft'}
            color={getStatusColor(data.status)}
            size="medium"
          />
          {firstRequest && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/requests/${firstRequest.id}/view`)}
            >
              View Request
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={() => handleDownloadPDF('customer')}>
            Customer Invoice
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={() => handleDownloadPDF('insurance')}>
            Insurance Invoice
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/invoices/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      {/* Invoice Details */}
      <Box className="print-area">
        <DarbInvoiceTemplate
          invoice={invoice}
          lineItems={lineItems}
          billTo={{
            name: billTo?.name ?? undefined,
            address: billTo?.address ?? undefined,
            phone: billTo?.phone ?? undefined,
            email: billTo?.email ?? undefined,
            vatNumber: billTo?.vatNumber ?? undefined,
            policyNumber: billTo?.policyNumber ?? undefined,
            vinNumber: billTo?.vinNumber ?? undefined,
            insuranceCompany: billTo?.insuranceCompany ?? undefined,
          }}
          invoiceType={['insurance', 'insurance_company_monthly', 'insurance_customer'].includes(data.invoiceType) ? 'insurance' : 'customer'}
          relatedRequest={firstRequest}
        />
      </Box>
    </Box>
  );
};

export default InvoiceView;

